<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useQuery } from '@tanstack/vue-query'
import { useRouteParams, useRouteQuery } from '@vueuse/router'

import CustomerApiKeys from '#customers/api-keys/components/CustomerApiKeys.vue'
import CustomerAccount from '#customers/components/CustomerAccount.vue'
import CustomerAiUsage from '#customers/components/CustomerAiUsage.vue'
import CustomerBilling from '#customers/components/billing/CustomerBilling.vue'
import CustomerDeliveryBundle from '#customers/delivery-bundle/components/CustomerDeliveryBundle.vue'
import CustomerFiles from '#customers/files/components/CustomerFiles.vue'
import CustomerOnboarding from '#customers/onboarding/components/CustomerOnboarding.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import { usePermissions } from '#shared/composables/use-permissions'
import { supplierClassificationClient } from '#shared/services/api/supplier-classification.client'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '#ui/tabs'

definePage({
  meta: { requiresAuth: true, title: 'Customer' },
})

type Tab =
  'account' | 'ai-usage' | 'api-keys' | 'billing' | 'delivery-bundle' | 'files' | 'onboarding'

const { canSeeBilling } = usePermissions()

const tabs = computed(() => {
  const entries: { id: Tab; label: string }[] = [
    { id: 'account', label: 'Account' },
    { id: 'onboarding', label: 'Onboarding' },
    { id: 'delivery-bundle', label: 'Delivery bundle' },
    { id: 'files', label: 'Files' },
    { id: 'api-keys', label: 'API keys' },
    { id: 'ai-usage', label: 'AI usage' },
  ]
  if (canSeeBilling.value) entries.splice(1, 0, { id: 'billing', label: 'Billing' })
  return entries
})

const organizationId = useRouteParams<string>('organizationId')
const requestedTab = useRouteQuery<Tab>('tab', 'account')

const tab = computed<Tab>({
  get: () =>
    tabs.value.some((entry) => entry.id === requestedTab.value) ? requestedTab.value : 'account',
  set: (value) => {
    requestedTab.value = value
  },
})

const detailsQuery = useQuery({
  queryKey: computed(() => ['organizations', organizationId.value] as const),
  queryFn: async () => {
    const { data, error } = await supplierClassificationClient.GET(
      '/api/admin/organizations/{organizationId}',
      { params: { path: { organizationId: organizationId.value } } },
    )
    if (error) throw error
    return data
  },
})

const name = computed(() => detailsQuery.data.value?.database.name)
const logoSrc = computed(() => detailsQuery.data.value?.auth0.logoUrl ?? '')

const logoBroken = ref(false)
watch(logoSrc, () => {
  logoBroken.value = false
})
</script>

<template>
  <PageHeader :title="name ?? 'Customer'">
    <template v-if="logoSrc && !logoBroken" #leading>
      <img
        :src="logoSrc"
        alt=""
        class="size-10 rounded-md border object-contain"
        @error="logoBroken = true"
      />
    </template>
    <template #description>
      <span class="type-mono">{{ organizationId }}</span>
    </template>
  </PageHeader>

  <!-- Line tabs over a full-width rule: these switch the page, they do not filter it. -->
  <Tabs v-model="tab" class="gap-6">
    <div class="border-b">
      <TabsList variant="line">
        <TabsTrigger v-for="entry in tabs" :key="entry.id" :value="entry.id">
          {{ entry.label }}
        </TabsTrigger>
      </TabsList>
    </div>

    <TabsContent value="account">
      <CustomerAccount :organization-id="organizationId" />
    </TabsContent>
    <TabsContent v-if="canSeeBilling" value="billing">
      <CustomerBilling :organization-id="organizationId" />
    </TabsContent>
    <TabsContent value="onboarding">
      <CustomerOnboarding :organization-id="organizationId" />
    </TabsContent>
    <TabsContent value="delivery-bundle">
      <CustomerDeliveryBundle :organization-id="organizationId" />
    </TabsContent>
    <TabsContent value="files">
      <CustomerFiles :organization-id="organizationId" />
    </TabsContent>
    <TabsContent value="api-keys">
      <CustomerApiKeys :organization-id="organizationId" />
    </TabsContent>
    <TabsContent value="ai-usage">
      <CustomerAiUsage :organization-id="organizationId" />
    </TabsContent>
  </Tabs>
</template>
